import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  Divider,
  Dropdown,
  FormInput,
  Typography,
  classNames,
  renderIcon,
  Alert,
  StackedList,
  UserAlertListDataItem,
} from '@ecdlink/ui';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { staticDataSelectors } from '@store/static-data';
import { childrenSelectors } from '@store/children';
import { caregiverSelectors } from '@/store/caregiver';
import * as styles from './care-giver-information-form.styles';
import {
  CareGiverInformationFormModel,
  careGiverInformationFormSchema,
} from '@schemas/child/child-registration/care-giver-information-form';
import { CareGiverInformationFormProps } from './care-giver-information-form.types';
import { ChildService } from '@/services/ChildService';
import { authSelectors } from '@/store/auth';
import { format, parseISO } from 'date-fns';
import { ChildMatchingDto } from '@pages/child/practitioner-child-registration/child-basic-info/child-basic-info.types';
import { classroomsSelectors } from '@/store/classroom';

export const CareGiverInformationForm: React.FC<
  CareGiverInformationFormProps
> = ({
  careGiverInformation,
  childName,
  childData,
  isOnline,
  onSubmit,
  finishRegistration,
}) => {
  const relations = useSelector(staticDataSelectors.getRelations);
  const [idFieldVisible, setIdFieldVisible] = useState(true);
  const userAuth = useSelector(authSelectors.getAuthUser);
  const [checkChild, setCheckChild] = useState<ChildMatchingDto | null>(null);
  const [listItems, setListItems] = useState<any[] | null>(null);
  const children = useSelector(childrenSelectors.getChildren);
  const caregivers = useSelector(caregiverSelectors.getCaregivers);
  const childUsers = useSelector(childrenSelectors.getChildUsers);
  const classroom = useSelector(classroomsSelectors.getClassroom);

  const {
    getValues: getCareGiverInformationFormValues,
    setValue: setCareGiverInformationFormValue,
    register: CareGiverInformationFormRegister,
    reset: resetCareGiverInformationForm,
    watch: watchCareGiverInformationForm,
    trigger: triggerCareGiverInformationForm,
    formState: careGiverInformationFormState,
  } = useForm<CareGiverInformationFormModel>({
    resolver: yupResolver(careGiverInformationFormSchema),
    mode: 'onChange',
    defaultValues: { ...careGiverInformation, preferId: true },
  });

  useEffect(() => {
    const prefersId =
      careGiverInformation?.preferId ??
      !careGiverInformation?.careGiverPassportField;

    resetCareGiverInformationForm({
      ...careGiverInformation,
      preferId: prefersId,
    });
    setIdFieldVisible(prefersId);
    // Keep duplicate state aligned with the latest active form snapshot.
    setCheckChild(null);
    setListItems(null);
  }, [careGiverInformation, resetCareGiverInformationForm]);

  const watchedFirstName = watchCareGiverInformationForm('firstname');
  const watchedSurname = watchCareGiverInformationForm('surname');
  const watchedId = watchCareGiverInformationForm('careGiverIdField');
  const watchedPassport = watchCareGiverInformationForm(
    'careGiverPassportField'
  );
  const watchedPhone = watchCareGiverInformationForm('phoneNumber');

  useEffect(() => {
    if (!checkChild && !listItems) return;

    setCheckChild(null);
    setListItems(null);
  }, [
    checkChild,
    listItems,
    watchedFirstName,
    watchedSurname,
    watchedId,
    watchedPassport,
    watchedPhone,
  ]);

  const setNewStackListItems = (checkChild: ChildMatchingDto) => {
    let dateString = 'unknown';

    if (checkChild?.createdByDate) {
      dateString = format(new Date(checkChild?.createdByDate!), 'dd MMM yyyy');
    }

    const list: UserAlertListDataItem[] = [
      {
        profileDataUrl: checkChild?.profileImageUrl || '',
        title: `${checkChild?.fullName}` || '',
        subTitle: `Added by ${checkChild?.practitionerName} on ${dateString}.`,
        profileText:
          `${
            checkChild?.fullName?.split(' ')[0] || ''.toUpperCase()
          }${checkChild?.fullName?.split(' ')[1].toUpperCase()}` || '',
        alertSeverity: 'none',
        avatarColor: '',
        breaksSubtitleLine: true,
      },
    ];

    setListItems(list);
  };

  const { isValid } = careGiverInformationFormState;

  const parseDate = (dateString: string | Date | undefined) => {
    if (!dateString) return null;
    return new Date(dateString);
  };

  const checkDuplicateChildOffline = (
    firstName: string,
    surname: string,
    dob?: string,
    idNo?: string,
    caregiverId?: string,
    caregiverPhoneNumber?: string,
    genderId?: string,
    childId?: string,
    careGiverPassport?: string
  ): ChildMatchingDto | null => {
    const normalizeValue = (value?: string) =>
      (value || '').toLowerCase().replace(/\s+/g, '').trim();

    type ChildUserLike = {
      id?: string;
      firstName?: string;
      surname?: string;
      idNumber?: string;
      dateOfBirth?: string | Date;
      genderId?: string;
      profileImageUrl?: string;
    };

    type CaregiverLike = {
      idNumber?: string;
      passportNumber?: string;
      phoneNumber?: string;
      firstName?: string;
      surname?: string;
    };

    const resolveChildUser = (child: {
      user?: ChildUserLike;
      userId?: string;
    }): ChildUserLike | undefined =>
      child.user ?? childUsers.find((u) => u.id === child.userId);

    const resolveCaregiver = (child: {
      caregiver?: CaregiverLike;
      caregiverId?: string;
    }): CaregiverLike | undefined =>
      child.caregiver ?? caregivers?.find((c) => c.id === child.caregiverId);

    const searchFirstName = firstName?.toLowerCase().trim();
    const searchSurname = surname?.toLowerCase().trim();
    const normalizedChildIdNo = normalizeValue(idNo);
    const normalizedCaregiverId = normalizeValue(caregiverId);
    const normalizedCaregiverPassport = normalizeValue(careGiverPassport);
    const normalizedCaregiverPhone = normalizeValue(caregiverPhoneNumber);

    if (idNo) {
      var idMatch = children?.find((child) => {
        if (childId && child.id === childId) return false;

        const user = resolveChildUser(child);

        if (normalizeValue(user?.idNumber) === normalizedChildIdNo) return true;

        return false;
      });

      if (idMatch) {
        const user = resolveChildUser(idMatch);

        return {
          childUserId: idMatch.userId,
          fullName: user ? `${user.firstName} ${user.surname}` : '',
          dateOfBirth: user?.dateOfBirth?.toString() ?? '',
          profileImageUrl: user?.profileImageUrl,
        };
      }
    }

    const allConditionMatches = children?.filter((child) => {
      if (childId && child.id === childId) return false;

      const user = resolveChildUser(child);

      const nameMatches =
        (!searchFirstName ||
          user?.firstName?.toLowerCase().trim() === searchFirstName) &&
        (!searchSurname ||
          user?.surname?.toLowerCase().trim() === searchSurname);

      const caregiver = resolveCaregiver(child);

      const dob1 = parseDate(dob);
      const dob2 = parseDate(user?.dateOfBirth);

      const dobMatches =
        dob1 !== null &&
        dob2 !== null &&
        dob1.getFullYear() === dob2.getFullYear() &&
        dob1.getMonth() === dob2.getMonth() &&
        dob1.getDate() === dob2.getDate();

      const caregiverMatches =
        (!normalizedCaregiverId &&
          !normalizedCaregiverPassport &&
          !normalizedCaregiverPhone) ||
        (caregiver
          ? normalizeValue(caregiver.idNumber) === normalizedCaregiverId ||
            normalizeValue(caregiver.passportNumber) ===
              normalizedCaregiverPassport ||
            normalizeValue(caregiver.phoneNumber) ===
              normalizedCaregiverPhone ||
            normalizeValue(caregiver.firstName) === searchFirstName ||
            normalizeValue(caregiver.surname) === searchSurname
          : false);
      const genderMatches = !genderId || user?.genderId === genderId;

      return nameMatches && dobMatches && caregiverMatches && genderMatches;
    });

    if (allConditionMatches && allConditionMatches.length > 0) {
      const matchedChild = allConditionMatches[0];
      const matchedChildUser = resolveChildUser(matchedChild);

      return {
        childUserId: matchedChild.userId,
        fullName: `${matchedChildUser?.firstName} ${matchedChildUser?.surname}`,
        dateOfBirth: matchedChildUser?.dateOfBirth?.toString() ?? '',
        profileImageUrl: matchedChildUser?.profileImageUrl,
      };
    }

    return null;
  };

  const formatDate = (dateValue: string | Date | undefined) => {
    if (!dateValue) return '';

    const date =
      typeof dateValue === 'string' ? parseISO(dateValue) : dateValue;

    return format(date, 'yyyy-MM-dd');
  };

  const handleFormSubmit = async (action: 'continue' | 'finish') => {
    const isFormValid = await triggerCareGiverInformationForm();
    if (!isFormValid) return;

    const careGiverInformationFormValues = getCareGiverInformationFormValues();

    var careGiverIdField = careGiverInformationFormValues.careGiverIdField;
    var caregiverPassportField =
      careGiverInformationFormValues.careGiverPassportField;
    var caregiverPhoneNumber = careGiverInformationFormValues.phoneNumber;

    careGiverIdField = !careGiverIdField
      ? caregiverPassportField
      : careGiverIdField;

    if (careGiverIdField === '') {
      careGiverIdField = careGiverInformationFormValues.careGiverPassportField;
    }

    try {
      let duplicate: ChildMatchingDto | null = null;

      let childDobFormatted = formatDate(childData.dob);

      if (isOnline) {
        duplicate = await new ChildService(
          userAuth?.auth_token!
        ).checkDuplicateChild(
          childData.firstName,
          childData.surname,
          childDobFormatted,
          childData.idNo,
          careGiverIdField,
          childData.genderId,
          childData.childId
        );
      } else {
        duplicate = checkDuplicateChildOffline(
          childData.firstName,
          childData.surname,
          childDobFormatted,
          childData.idNo,
          careGiverIdField,
          caregiverPhoneNumber,
          childData.genderId,
          childData.childId,
          caregiverPassportField
        );
      }

      if (duplicate) {
        setCheckChild(duplicate);
        setNewStackListItems(duplicate);
        return;
      } else {
        setCheckChild(null);
        setListItems(null);
      }
    } catch (error) {
      console.error('Error during duplicate check:', error);
    } finally {
      console.log('Duplicate check completed');
    }

    if (action === 'finish') {
      careGiverInformationFormValues.finishRegistration = true;
    } else {
      careGiverInformationFormValues.finishRegistration = false;
    }

    if (onSubmit) {
      onSubmit(careGiverInformationFormValues);
    }
  };

  const toggleIdAndpassport = (visible: boolean) => {
    const flag = !visible;
    setCareGiverInformationFormValue(
      flag ? 'careGiverPassportField' : 'careGiverIdField',
      '',
      {
        shouldValidate: true,
      }
    );
    setCareGiverInformationFormValue('preferId', flag, {
      shouldValidate: true,
    });
    setIdFieldVisible(flag);
  };

  return (
    <div className={styles.wrapper}>
      <Typography type={'h1'} text={'Primary caregiver'} color={'primary'} />
      <Typography type={'h2'} text={'Details'} color={'textMid'} />

      <Dropdown
        placeholder={'Select relationship'}
        list={
          (relations &&
            relations.map((relation) => {
              return { label: relation.description, value: relation.id };
            })) ||
          []
        }
        fullWidth
        fillType="clear"
        label={`Relationship to ${childName || 'child'}?`}
        selectedValue={getCareGiverInformationFormValues().relationId}
        className={'mt-3'}
        onChange={(item: any) => {
          setCareGiverInformationFormValue('relationId', item, {
            shouldValidate: true,
          });
        }}
      />
      <FormInput<CareGiverInformationFormModel>
        label={'First name'}
        className={'mt-4'}
        register={CareGiverInformationFormRegister}
        nameProp={'firstname'}
        error={careGiverInformationFormState.errors['firstname']}
        placeholder={'First name'}
      />
      <FormInput<CareGiverInformationFormModel>
        label={'Surname'}
        className={'mt-3'}
        register={CareGiverInformationFormRegister}
        nameProp={'surname'}
        error={careGiverInformationFormState.errors['surname']}
        placeholder={'Surname/family name'}
      />
      {idFieldVisible && (
        <FormInput<CareGiverInformationFormModel>
          label={"Caregiver's ID number"}
          className={'mt-4'}
          nameProp={'careGiverIdField'}
          register={CareGiverInformationFormRegister}
          error={careGiverInformationFormState.errors['careGiverIdField']}
          placeholder={'E.g. 190101 0000 000'}
        />
      )}
      {!idFieldVisible && (
        <FormInput<CareGiverInformationFormModel>
          label={'Passport number'}
          className={'mt-4'}
          visible={true}
          nameProp={'careGiverPassportField'}
          error={careGiverInformationFormState.errors['careGiverPassportField']}
          register={CareGiverInformationFormRegister}
        />
      )}
      {!idFieldVisible && (
        <Button
          className={'mt-3 mb-2'}
          type="outlined"
          color="primary"
          background={'transparent'}
          size="small"
          onClick={() => toggleIdAndpassport(idFieldVisible)}
        >
          <Typography
            type="small"
            color="primary"
            text={'Enter ID number instead'}
          ></Typography>
        </Button>
      )}
      {idFieldVisible && (
        <Button
          className={'mt-3 mb-2'}
          type="outlined"
          color="primary"
          size="small"
          background={'transparent'}
          onClick={() => toggleIdAndpassport(idFieldVisible)}
        >
          <Typography
            type="small"
            color="primary"
            text={'Enter passport number instead'}
          ></Typography>
        </Button>
      )}
      <FormInput<CareGiverInformationFormModel>
        label={'Cellphone number'}
        className={'mt-1'}
        register={CareGiverInformationFormRegister}
        nameProp={'phoneNumber'}
        error={careGiverInformationFormState.errors['phoneNumber']}
        placeholder={'E.g. 082 345 6789'}
      />
      {checkChild && (
        <div>
          <Alert
            title={`There is already a child named ${checkChild?.fullName} at ${
              classroom?.name || 'this site'
            }${
              checkChild?.dateOfBirth &&
              !isNaN(new Date(checkChild.dateOfBirth).getTime()) &&
              !checkChild.dateOfBirth.includes('0001-01-01')
                ? `, born on ${format(
                    new Date(checkChild.dateOfBirth),
                    'dd MMM yyyy'
                  )}`
                : ''
            }.`}
            type="warning"
            list={[
              'You have already registered a child with these details. To avoid duplicates, a new child has not been created.',
            ]}
            className={'mt-4'}
          />
          {listItems && (
            <StackedList
              className={'my-4'}
              listItems={listItems}
              type={'UserAlertList'}
            />
          )}
        </div>
      )}
      <div className={'py-4'}>
        <Divider></Divider>
      </div>
      {/* <Button
        onClick={handleFormSubmit}
        className="w-full"
        size="small"
        color="primary"
        type="filled"
        disabled={!isValid || !!checkChild}
      >
        {renderIcon('ArrowCircleRightIcon', classNames('h-5 w-5 text-white'))}
        <Typography type="h6" className="ml-2" text="Add more child information" color="white" />
      </Button> */}
      <div className="flex flex-col gap-3">
        <Button
          onClick={() => handleFormSubmit('continue')}
          className="w-full"
          size="small"
          color="primary"
          type="filled"
          disabled={!isValid || !!checkChild}
        >
          {renderIcon('ArrowCircleRightIcon', classNames('h-5 w-5 text-white'))}
          <Typography
            type="h6"
            className="ml-2"
            text="Add more child information"
            color="white"
          />
        </Button>

        <Button
          onClick={() => handleFormSubmit('finish')}
          className="w-full"
          size="small"
          color="primary"
          type="filled"
          disabled={!isValid || !!checkChild}
        >
          {renderIcon('ArrowCircleRightIcon', classNames('h-5 w-5 text-white'))}
          <Typography
            type="h6"
            className="ml-2"
            text="Finish registration"
            color="white"
          />
        </Button>
      </div>
    </div>
  );
};
